import React, { CSSProperties, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { classList } from 'services/styles'
import AnimBox from 'components/AnimBox'
import { trackError, trackEvent } from 'services/analytics'
import { useFragment, useSubscription } from 'react-relay/hooks'
import { graphql } from 'relay-runtime'
import { Video_Video$key } from '__generated__/Video_Video.graphql'
import { useTheme } from '@mui/material/styles'
import { useResponsiveBreakpoint } from 'hooks/useResponsiveBreakpoint'
import { Typography } from '@mui/material'
import { Nullable } from 'types'
import { getGraphQLBaseURL } from 'api/url'
import { Video_Subscription } from '__generated__/Video_Subscription.graphql'
import useStyles from './styles'
import PlayIcon from './PlayIcon'
import Picture, { IPictureProps } from '../Picture'

interface IVideoProps {
  videoFragmentRef?: Nullable<Video_Video$key>
  /** @deprecated use videoFragmentRef */
  videoUrl?: string
  posterUrl?: string
  fullWidth?: boolean
  disabled?: boolean
  rounded?: boolean
  iconSize?: 'small' | 'large'
  preload?: 'metadata' | 'auto' | 'none'
  title?: string
  compact?: boolean
  pictureProps?: Omit<IPictureProps, 'onClick' | 'onError' | 'src'>
  className?: string
  style?: CSSProperties
  overlay?: boolean
  cover?: boolean
}

function Video(props: IVideoProps) {
  const { classes } = useStyles()
  const theme = useTheme()
  const {
    videoFragmentRef,
    videoUrl: videoUrlProp,
    posterUrl,
    rounded = true,
    className,
    style = {},
    title,
    iconSize,
    preload = 'metadata',
    compact,
    disabled,
    pictureProps = {},
    overlay = true,
    fullWidth,
    cover,
  } = props

  const isMobile = useResponsiveBreakpoint(theme.breakpoints.values.sm)

  const video = useFragment(graphql`
    fragment Video_Video on Video {
      id
      url
      optimizedUrl
      posterUrl
      enableSubtitles
    }
  `, videoFragmentRef)

  const videoElement = useRef<HTMLVideoElement>(null)

  const [onVideoEndedTimeoutHandle, setOnVideoEndedTimeoutHandle] = useState<number | null>(null)
  const [isEnabled, setIsEnabled] = useState(false)
  const [autoPlay, setAutoPlay] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isUnplayable, setIsUnplayable] = useState(false)
  const [isPosterFailed, setIsPosterFailed] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const [videoErrorType, setVideoErrorType] = useState<null | 'network' | 'format'>(null)

  const triggerPlayOnVideoElement = useCallback(() => {
    try {
      if (videoElement.current) {
        const playPromise = videoElement.current.play()
        // Note: on IE11 play() does not return anything
        if (typeof playPromise?.catch === 'function') playPromise.catch(err => console.log(err))
      }
    } catch (error) {
      trackError(error)
    }
  }, [])

  const handlePlayClick = useCallback(() => {
    if (disabled || isUnplayable) return

    // Setting autoPlay to true is required in case the video element is absent (due to the poster being shown)
    // It will cause the video to immediately play once mounted into the DOM
    setAutoPlay(true)
    setIsEnabled(true)

    // Manually trigger the play event, this is in case the video element is already mounted into the DOM
    // For example when the poster is not provided, so the video element itself is mounted/displayed immediately
    triggerPlayOnVideoElement()

    trackEvent('Video Play Start')
  }, [disabled, isUnplayable, triggerPlayOnVideoElement])

  const handleImgError = useCallback(() => { setIsPosterFailed(true) }, [])

  const handleError = useCallback(async () => {
    if (retryCount < 2 && videoElement.current) {
      videoElement.current.load()
      setRetryCount(retryCount + 1)
      return
    }

    // If this point is reached, we've already retried the video element to the maximum number of retries
    // To test if this is a format error or a network error, we can try to 'fetch' the video file
    // If the request also fails, we can assume it's a network error, otherwise we can assume it's a format error
    let url = videoUrlProp || video?.optimizedUrl
    let errorType: 'network' | 'format' = 'network'

    try {
      if (url) {
        const res = await fetch(url, { method: 'HEAD' })

        if (res.ok) {
          errorType = 'format'
        } else {
          errorType = 'network'
        }
      }
    } catch (err) {
      // If fetch rejects, assume network error
      errorType = 'network'
    }

    trackError(new Error('Failed to load video'), 'medium', {
      videoId: video?.id,
      videoUrl: video?.url,
      videoOptimizedUrl: video?.optimizedUrl,
    })

    setVideoErrorType(errorType)
    setIsUnplayable(true)
  }, [video, videoUrlProp, retryCount])

  const handleVideoPlay = useCallback(() => {
    setIsPlaying(true)

    if (onVideoEndedTimeoutHandle) window.clearTimeout(onVideoEndedTimeoutHandle)

    trackEvent('Video Play')

    // Auto-enable subtitles if available
    if (videoElement.current) {
      const tracks = videoElement.current.textTracks
      if (tracks.length > 0) {
        tracks[0].mode = 'showing'
      }
    }

    // Stop every other Video
    const videos = document.getElementsByTagName('video')

    for (let i = 0; i < videos.length; i++) {
      if (!videos[i].paused && videos[i] !== videoElement.current) {
        videos[i].pause()
        videos[i].currentTime = 0
      }
    }
  }, [onVideoEndedTimeoutHandle])

  const handleVideoPause = useCallback(() => {
    setIsPlaying(false)
    trackEvent('Video Pause')
  }, [])

  const handleVideoEnded = useCallback(() => {
    if (onVideoEndedTimeoutHandle) window.clearTimeout(onVideoEndedTimeoutHandle)
    const handle = window.setTimeout(() => {
      setIsEnabled(false)
    }, 1000)
    setOnVideoEndedTimeoutHandle(handle)
    trackEvent('Video Ended')
  }, [onVideoEndedTimeoutHandle])

  const videoUrl = videoUrlProp || video?.optimizedUrl
  const posterSrc = posterUrl || video?.posterUrl
  const enablePoster = posterSrc && !isPosterFailed
  const showingPoster = enablePoster && !isEnabled
  const sizeLarge = !iconSize ? (!isMobile && !title) : iconSize === 'large'
  const size = sizeLarge ? 'large' : 'small' as const

  const mergedStyles = useMemo(() => ({
    borderRadius: rounded ? undefined : 0,
    ...style,
  }), [style, rounded])

  useEffect(() => {
    // Reset the video state when the video url changes
    // This is needed in some use cases otherwise if the video is playing (isEnabled is true)
    // and the url changes, the player will still show the old video.
    setIsEnabled(false)
  }, [videoUrl])

  useEffect(() => {
    // Preload the video file automatically
    if (preload !== 'none' && videoUrl) {
      const videoTempElement = document.createElement('video')
      videoTempElement.src = videoUrl
      videoTempElement.preload = preload
    }
  }, [videoUrl, preload])

  useSubscription<Video_Subscription>({
    subscription: graphql`
      subscription Video_Subscription($id: ID) {
        videoUpdated(videoId: $id) {
          video {
            ...Video_Video
          }
        }
      }
    `,
    variables: {
      id: video?.id,
    },
  })

  if (!videoUrl) return null

  return (
    <div className={classList(classes.container, className)} style={mergedStyles}>
      {overlay && (
        <div className={classes.overlay} style={{ opacity: isEnabled ? 0.5 : 1 }} />
      )}

      <div
        className={classList(classes.play, sizeLarge ? undefined : classes.playCompact, title ? undefined : classes.playCentered)}
        onClick={handlePlayClick}
        role="button"
        style={isEnabled ? { pointerEvents: 'none' } : undefined}
      >
        {!isEnabled && (
          <AnimBox grow delay={100} offset={-100} style={{ alignSelf: 'flex-start' }}>
            <PlayIcon size={size} />
          </AnimBox>
        )}

        {title && (
          <AnimBox heavySlideRight delay={100} offset={-100}>
            <Typography
              variant="h6"
              component="div"
              className={classList(classes.title, compact ? classes.titleCompact : undefined)}
              title={(typeof title === 'string') ? title : undefined}
              style={isPlaying ? { opacity: 0, transition: 'opacity 1s ease', transitionDelay: '1s' } : { transition: 'opacity 0.5s ease' }}
            >
              {title}
            </Typography>
          </AnimBox>
        )}
      </div>

      {posterSrc && (
        <Picture
          width={fullWidth ? undefined : 400}
          className={classList(classes.poster, overlay ? undefined : classes.disableOverlay)}
          {...pictureProps}
          style={{ visibility: showingPoster ? 'visible' : 'hidden', width: (fullWidth || cover) ? '100%' : 'auto', objectFit: cover ? 'cover' : undefined, ...pictureProps.style }}
          src={posterSrc}
          preload={preload !== 'none'}
          onClick={handlePlayClick}
          onError={handleImgError}
        />
      )}

      {videoUrl && !showingPoster && (
        // Explanation for crossOrigin: https://developers.google.com/web/tools/workbox/guides/advanced-recipes#warm_the_runtime_cache
        <video
          style={{ pointerEvents: disabled ? 'none' : undefined, objectFit: cover ? 'cover' : undefined }}
          controls={isEnabled}
          className={classes.video}
          preload={preload}
          ref={videoElement}
          onError={handleError}
          onEnded={handleVideoEnded}
          onPlay={handleVideoPlay}
          onPause={handleVideoPause}
          autoPlay={autoPlay}
        >
          <source
            src={enablePoster ? videoUrl : `${videoUrl}#t=0.25`}
            type="video/mp4"
          />
          {video?.id && video.enableSubtitles && (
            <track
              kind="subtitles"
              src={`${getGraphQLBaseURL()}/subtitles/${video.id}`}
              srcLang="en"
              label="English (Auto-generated)"
            />
          )}
          Your browser does not support HTML5 video.
        </video>
      )}

      {isUnplayable && (
        <div className={classes.unplayable}>
          {videoErrorType === 'network' && (
            <React.Fragment>Failed to load video. Please check your connection or try again later.</React.Fragment>
          )}

          {videoErrorType === 'format' && (
            <React.Fragment>Your browser does not support this video format.</React.Fragment>
          )}

          {!videoErrorType && (
            <React.Fragment>An unknown error occurred while loading the video.</React.Fragment>
          )}
        </div>
      )}
    </div>
  )
}

export default memo(Video)
